import { counted } from '../plural';
import type { Messages } from '../shape';

export const views = {
  search: {
    placeholder: 'Поиск по этому хранилищу',
    results: (count: number) => counted(count, ['результат', 'результата', 'результатов']),
    // «12 проиндексировано» съедает половину строки ввода, а смысл тот же.
    indexed: (count: number) => `${count} в индексе`,
    allNotes: 'Все заметки',
    tag: (name: string) => `тег: #${name}`,
    local: 'Поиск идёт локально по расшифрованному индексу — ни один запрос не покидает это устройство',
    downloading: (covered: number, total: number) =>
      `· индекс ${covered}/${total} — ещё загружается`,
    none: 'Ничего не нашлось.',
    noneYet: 'Ничего не нашлось. Индекс ещё наполняется — попробуйте ещё раз через минуту.',
  },

  graph: {
    title: 'Граф',
    canvas: 'Граф заметок',
    drawing: 'Рисуем граф…',
    empty:
      'Заметок пока нет. Ссылки появятся здесь, когда заметки начнут ссылаться друг на друга через [[название]].',
    legend: (notes: number, links: number) =>
      `${counted(notes, ['заметка', 'заметки', 'заметок'])} · ${counted(links, ['ссылка', 'ссылки', 'ссылок'])}`,
    legendLocked: (notes: number, links: number, locked: number) =>
      `${counted(notes, ['заметка', 'заметки', 'заметок'])} · ${counted(links, ['ссылка', 'ссылки', 'ссылок'])} · ${locked} без ключа`,
    node: (name: string, links: number) =>
      `${name} · ${counted(links, ['ссылка', 'ссылки', 'ссылок'])}`,
    legendHidden: (hidden: number) => `· ${hidden} без связей`,
    moreGroups: (count: number) => `ещё ${counted(count, ['папка', 'папки', 'папок'])}`,
    localOpen: 'Локальный граф открытой заметки',
    localTitle: 'Локальный граф',
    localClose: 'Вернуться к полному графу',
    depth: 'Глубина',
    depthTip: (depth: number) =>
      `Заметки не дальше ${counted(depth, ['ссылки', 'ссылок', 'ссылок'])} от центра`,
    zoomIn: 'Приблизить',
    zoomOut: 'Отдалить',
    fit: 'Вписать в панель',
    reset: 'Сбросить раскладку',
    orphans: 'Одиночные',
    orphansTip: 'Одиночные — рисовать заметки, на которые никто не ссылается',
    noLinks:
      'Ни одна заметка пока ни на что не ссылается. Включите одиночные, чтобы увидеть их все, или сошлитесь из одной заметки на другую через [[название]].',
    hint: 'Колесо — масштаб, перетаскивание холста — сдвиг, перетаскивание заметки закрепляет её на месте. Shift+клик по заметке оставляет только её соседей, Esc возвращает весь граф. Сброс раскладки отпускает все закреплённые.',
    revealsLocked:
      'Пунктирные узлы — заметки, ключа от которых у вас нет. Они нарисованы без имени и без номера: граф, который их спрятал бы, показывал бы связанные заметки одинокими.',
    hidesLocked:
      'Это хранилище не рисует заметки, которые вы не можете открыть, поэтому перед вами ваш срез графа, а не его форма.',
  },

  trash: {
    title: 'Корзина',
    items: (count: number) => counted(count, ['объект', 'объекта', 'объектов']),
    empty:
      'Ничего не удалено. Всё, что вы убираете, попадает сюда — по-прежнему зашифрованным — и ждёт, пока вы его вернёте или уничтожите.',
    noKey: 'Нет ключа',
    folder: 'Папка',
    note: 'Заметка',
    restore: 'Восстановить',
    purge: 'Удалить навсегда',
    purgeArmed: 'Уничтожить безвозвратно',
    frozen:
      'Включён режим только для чтения: это просто список и ничего больше. Отключите его в меню аккаунта, чтобы что-то вернуть или уничтожить безвозвратно.',
    footer:
      'Восстановление папки возвращает и то, что лежало внутри. Удаление навсегда уничтожает шифротекст — копии, которая вернула бы его, нет нигде: ни здесь, ни на сервере.',
  },

  profile: {
    title: 'Профиль',
    back: 'Назад к заметкам',

    account: 'Аккаунт',
    interface: 'Интерфейс',
    keys: 'Ключи',
    passphrase: 'Парольная фраза',
    danger: 'Опасная зона',

    login: 'Логин',
    memberSince: 'Аккаунт создан',
    vaults: 'Хранилища',
    // Существительное подставляет подпись слева, поэтому здесь только «своё» и «общее».
    vaultsOwn: (owned: number) => counted(owned, ['своё', 'своих', 'своих']),
    vaultsShared: (owned: number, shared: number) =>
      `${counted(owned, ['своё', 'своих', 'своих'])} · ${counted(shared, ['общее', 'общих', 'общих'])}`,

    thisDevice: 'Это устройство',
    keyUnlocked: 'Ключ разблокирован',
    keyLocked: 'Ключ заблокирован',
    fingerprint: 'Отпечаток ключа',
    keyNote:
      'Ваша парольная фраза не покидает это устройство: она разворачивает мастер-ключ здесь, а сервер держит только завёрнутую копию.',
    lock: 'Заблокировать ключи',
    signOut: 'Выйти',

    displayName: 'Отображаемое имя',
    nameFailed: 'Имя сохранить не удалось.',

    language: 'Язык',
    languageNote:
      'Язык выбирается один раз, при загрузке, поэтому переключение перезагружает страницу. Всё набранное сохраняется до этого.',

    currentPassphrase: 'Текущая парольная фраза',
    newPassphrase: 'Новая парольная фраза',
    repeatPassphrase: 'Повторите новую фразу',
    // «Минимум», а не «не меньше»: последнее требует родительного падежа, и лесенка из трёх
    // форм на него не ложится — «не меньше 21 символа», но «не меньше 22 символов».
    passphraseNote: (min: number) =>
      `Минимум ${counted(min, ['символ', 'символа', 'символов'])}. Другие ваши устройства выходят из аккаунта, а код восстановления обновляется: новый показываем один раз, старый перестаёт работать.`,
    mismatch: 'Парольные фразы не совпадают.',
    reused: 'Это та же парольная фраза, что и сейчас.',
    changing: 'Меняем…',
    change: 'Сменить парольную фразу',
    passphraseFailed: 'Сменить парольную фразу не удалось.',

    dangerTitle: 'Удалить этот аккаунт',
    dangerBody:
      'Вместе с ним уйдут хранилища, которыми вы владеете, и всё, что в них есть, — для каждого их участника. За этим нет корзины и нет ключа, который что-то вернёт.',
    deleteAccount: 'Удалить аккаунт',
    deleteTitle: 'Удалить ваш аккаунт?',
    deleteBody:
      'Это уничтожит ваш аккаунт, все хранилища, которыми вы владеете, и всё их содержимое — для всех их участников. Сервер держит только шифротекст и удаляет его; ни один сохранённый кем-то ключ его не вернёт. Заметки, написанные вами в чужом хранилище, остаются с этим хранилищем.',
    confirmPassphrase: 'Подтвердите парольной фразой',
    deleting: 'Удаляем…',
    deleteFailed: 'Удалить аккаунт не удалось.',
  },
} satisfies Messages['views'];
